/**
 * =============================================================================
 * IMAGE THUMBNAIL GRID (shared — question grid + reply strips, PR6)
 * =============================================================================
 *
 * ADR-022 §9 display: a grid of click-to-enlarge thumbnails that opens the
 * shared {@link ImageLightbox}. ONE component backs both the question image grid
 * (`variant="primary"`) and the smaller, contextual per-reply strip
 * (`variant="compact"`), so their URL/label/CLS semantics cannot drift:
 *   - thumbnails use `thumbnailUrl ?? url`; the lightbox opens the full `url`
 *     (same versioned-URL semantics as question images);
 *   - accessible labels are ALWAYS positional/generic (`Question image 2`) —
 *     NEVER derived from an uploaded filename (ADR-022 §6.1/§9, G9);
 *   - fixed square cells reserve layout space before load (no CLS); the lightbox
 *     reserves via intrinsic width/height (ADR-022 §8);
 *   - `loading="lazy"` + `decoding="async"` keep long threads cheap.
 *
 * It renders NOTHING for an empty list, so callers can mount it unconditionally.
 * =============================================================================
 */

import React, { useState } from 'react';
import type { ImageAttachment } from '@find-my-part/types';
import { useStyleInjection } from '../../hooks';
import { ImageLightbox } from '../ImageLightbox/ImageLightbox';

export interface ImageThumbnailGridProps {
  images: ImageAttachment[];
  /**
   * Accessible NOUN for the generic per-image labels, e.g. "Question image" /
   * "Reply image". NEVER a filename (ADR-022 §6.1/§9).
   */
  labelNoun: string;
  /** Accessible group label, e.g. "Question images" / "Reply images". */
  groupLabel: string;
  /** `primary` = the question grid; `compact` = the smaller per-reply strip. */
  variant?: 'primary' | 'compact';
}

export function ImageThumbnailGrid({
  images,
  labelNoun,
  groupLabel,
  variant = 'primary',
}: ImageThumbnailGridProps): React.ReactElement | null {
  useStyleInjection(imageGridStyles, 'fmp-image-grid-styles');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (images.length === 0) return null;

  return (
    <>
      <div
        className={`fmp-image-grid${variant === 'compact' ? ' fmp-image-grid--compact' : ''}`}
        role="group"
        aria-label={groupLabel}
      >
        {images.map((image, index) => (
          <button
            key={image.attachmentId}
            type="button"
            className="fmp-image-grid__btn"
            onClick={() => setLightboxIndex(index)}
            aria-label={`View ${labelNoun} ${index + 1} of ${images.length} full size`}
          >
            <img
              src={image.thumbnailUrl ?? image.url}
              alt={`${labelNoun} ${index + 1}`}
              className="fmp-image-grid__img"
              loading="lazy"
              decoding="async"
              {...(image.width && image.height
                ? { width: image.width, height: image.height }
                : {})}
            />
          </button>
        ))}
      </div>

      {lightboxIndex !== null && (
        <ImageLightbox
          images={images.map((img) => ({ url: img.url, width: img.width, height: img.height }))}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          labelNoun={labelNoun}
        />
      )}
    </>
  );
}

// =============================================================================
// STYLES
// =============================================================================

// NB: comments inside this template literal ship as bundle bytes (unlike JS
// comments, which minify away), so keep it comment-free. Design notes for these
// rules live in the component docblock above: fixed square cells reserve layout
// (CLS-safe); the --compact variant is the smaller contextual reply strip.
const imageGridStyles = `
  :where(.fmp-widget-scope) .fmp-image-grid {
    display: flex;
    flex-wrap: wrap;
    gap: var(--fmp-space-sm);
    margin-top: var(--fmp-space-md);
  }
  :where(.fmp-widget-scope) .fmp-image-grid__btn {
    width: var(--fmp-size-96);
    height: var(--fmp-size-96);
    flex: 0 0 auto;
    padding: 0;
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-sm);
    background: var(--fmp-color-surface-muted);
    cursor: pointer;
    overflow: hidden;
    line-height: 0;
  }
  :where(.fmp-widget-scope) .fmp-image-grid__btn:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-offset);
  }
  :where(.fmp-widget-scope) .fmp-image-grid__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  :where(.fmp-widget-scope) .fmp-image-grid--compact {
    gap: var(--fmp-size-6);
    margin-top: var(--fmp-space-sm);
  }
  :where(.fmp-widget-scope) .fmp-image-grid--compact .fmp-image-grid__btn {
    width: var(--fmp-size-64);
    height: var(--fmp-size-64);
  }
`;
